import { Component } from '@theme/component';
import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  requiredRefs = ['quantityInput'];

  connectedCallback() {
    Component.prototype.connectedCallback.call(this);
    this.initialized = true;
    this.syncCurrentQuantityLabel();
    this.updateButtonStates();
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Gets current values from DOM for cart quantity selector.
   * Supports both select-based and input-based UI.
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const quantityInput = this.refs.quantityInput;

    return {
      min: parseInt(quantityInput.getAttribute('min') || '1', 10) || 1,
      max: quantityInput.getAttribute('max') ? parseInt(quantityInput.getAttribute('max'), 10) : null,
      step: parseInt(quantityInput.getAttribute('step') || '1', 10) || 1,
      value: parseInt(quantityInput.value || '0', 10) || 0,
      cartQuantity: parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10) || 0,
    };
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    return;
  }

  syncCurrentQuantityLabel() {
    const currentLabel = this.querySelector('[data-cart-quantity-current]');
    if (currentLabel) {
      currentLabel.textContent = this.refs.quantityInput.value || '1';
    }
  }

  /**
   * Handles cart quantity set event for select UI.
   * @param {Event} event
   */
  setQuantity(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    event.preventDefault();
    const { min } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(target.value, 10) || min));

    this.refs.quantityInput.value = quantity.toString();
    this.syncCurrentQuantityLabel();
    this.onQuantityChange();
  }

  /**
   * Handles custom cart quantity option clicks.
   * @param {Event} event
   */
  selectQuantity(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const option = target.closest('[data-quantity-option]');
    if (!(option instanceof HTMLElement)) return;

    event.preventDefault();

    const value = parseInt(option.getAttribute('data-quantity-option') || '', 10);
    const { min } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, value || min));

    this.refs.quantityInput.value = quantity.toString();
    this.syncCurrentQuantityLabel();
    this.onQuantityChange();

    const dropdown = this.querySelector('.quantity-select-menu');
    if (dropdown instanceof HTMLDetailsElement) {
      dropdown.open = false;
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
